import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { fetchAuthSession } from '@aws-amplify/auth';

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {
  private lambdaUrl = environment.lambdaUrls.pdfGenerator;

  constructor(private http: HttpClient) { }

  /**
   * Generate PDF from HTML content via Lambda proxy (API key stays server-side)
   * @param htmlContent The HTML string to convert to PDF
   * @param options Additional PDF options
   * @returns Observable<Blob> The PDF as a blob
   */
  generatePdf(htmlContent: string, options?: PdfShiftOptions): Observable<Blob> {
    return new Observable(observer => {
      this.callLambda(htmlContent, options).then(blob => {
        observer.next(blob);
        observer.complete();
      }).catch(err => {
        observer.error(err);
      });
    });
  }

  private async callLambda(htmlContent: string, options?: PdfShiftOptions): Promise<Blob> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const body = {
      source: htmlContent,
      format: options?.format || 'A4',
      margin: options?.margin || '15mm',
      landscape: options?.landscape || false,
      use_print: options?.use_print !== undefined ? options.use_print : true,
      ...(options?.header && { header: options.header }),
      ...(options?.footer && { footer: options.footer }),
      ...(options?.wait_for && { wait_for: options.wait_for }),
      ...(options?.delay && { delay: options.delay })
    };

    const response = await fetch(this.lambdaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'PDF generation failed');
    }

    return await response.blob();
  }

  /**
   * Generate PDF and download it
   * @param htmlContent The HTML string to convert
   * @param filename The filename for the downloaded PDF
   * @param options Additional PDF options
   */
  generateAndDownload(htmlContent: string, filename: string, options?: PdfShiftOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      this.generatePdf(htmlContent, options).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(url);
          resolve();
        },
        error: (err) => {
          console.error('PDF generation failed:', err);
          reject(err);
        }
      });
    });
  }

  /**
   * Generate PDF and open in new tab for preview
   * @param htmlContent The HTML string to convert
   * @param options Additional PDF options
   */
  generateAndPreview(htmlContent: string, options?: PdfShiftOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      this.generatePdf(htmlContent, options).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          resolve();
        },
        error: (err) => {
          console.error('PDF generation failed:', err);
          reject(err);
        }
      });
    });
  }
}

export interface PdfShiftOptions {
  margin?: string | { top?: string; right?: string; bottom?: string; left?: string };
  format?: 'A4' | 'Letter' | 'Legal';
  landscape?: boolean;
  use_print?: boolean;
  sandbox?: boolean;
  header?: { source: string; height?: string | number; start_at?: number };
  footer?: { source: string; height?: string | number; start_at?: number };
  wait_for?: string;
  delay?: number;
}
